/**
 * `<schema-browser>` — `SC-F412`, the schema browser.
 *
 * It renders the introspection cache (`SC-F411`) as something a person can read:
 * catalogs → schemas → tables → columns, with a search across every catalog. It is
 * mounted inside `<page-datasources>` rather than being a route of its own, because
 * the question it answers — "what is actually in the catalogs this instance
 * attaches?" — is the question the datasource list raises and cannot answer.
 *
 * # Two things it will not do
 *
 * **It never introspects.** Every navigation click is a read of the control plane's
 * own cache. Refreshing is a separate, per-instance rate-limited `POST` whose cost
 * lands on the customer's gateway, and it is offered as one explicit button rather
 * than folded into navigation — a browser that re-read on every click would turn
 * one curious user with a mouse into a fan-out of MCP calls against a production
 * warehouse.
 *
 * **It never presents a stale schema as current.** The cache's age is on the
 * header, the response's own `basis` sentence is shown verbatim beside it, and a
 * truncated tree says so. The failure this guards is specific and silent: an author
 * browses a schema, does not see `payments`, concludes the table does not exist,
 * and writes a `resources.tables` pattern that covers everything except the card
 * data. The policy compiles, loads and protects the wrong set, and nothing errors.
 *
 * All four states are here: loading (a spinner over the current level), empty (the
 * two distinguishable zero states — an empty cache versus a filter that matched
 * nothing), error (the `apierr` code AND the request id a reader quotes), and
 * populated.
 *
 * Stories: `SC-S407`, `SC-S507`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnColumn, BnRow, BnRowClickDetail } from "../components/index.js";

import {
  MIN_SEARCH_QUERY,
  browseSchema,
  refreshSchema,
  type BrowseLevel,
  type BrowseNode,
  type BrowseResponse,
} from "../api/datasources.js";
import { ApiError, parseError } from "../api/errors.js";

/** Keystroke debounce, so typing a table name is one request rather than eight. */
const SEARCH_DEBOUNCE_MS = 300;

/** The columns of each level. They differ because the levels differ: only a
 * catalog has a driver type, only a column has a SQL type, and only a search result
 * needs its own path shown. */
const LEVEL_COLUMNS: Record<BrowseLevel, BnColumn[]> = {
  catalogs: [
    { key: "name", label: "Catalog", mono: true },
    { key: "type", label: "Driver", width: "1px" },
    { key: "children", label: "Schemas", width: "1px", align: "right" },
    { key: "age", label: "Cache age", width: "1px" },
  ],
  schemas: [
    { key: "name", label: "Schema", mono: true },
    { key: "children", label: "Tables", width: "1px", align: "right" },
  ],
  tables: [
    { key: "name", label: "Table", mono: true },
    { key: "children", label: "Columns", width: "1px", align: "right" },
  ],
  columns: [
    { key: "name", label: "Column", mono: true },
    { key: "type", label: "SQL type", mono: true },
  ],
  search: [
    { key: "path", label: "Path", mono: true },
    { key: "kind", label: "Object", width: "1px" },
    { key: "type", label: "SQL type", mono: true, width: "1px" },
  ],
};

/** What the next level down is called, for the "n schemas" style cell and for the
 * drill-down. `columns` is a leaf and has no next level. */
const NEXT_LEVEL: Partial<Record<BrowseLevel, BrowseLevel>> = {
  catalogs: "schemas",
  schemas: "tables",
  tables: "columns",
};

@customElement("schema-browser")
export class SchemaBrowser extends LitElement {
  @property() instanceId = "";

  /** Whether the caller may trigger a refresh. A viewer may read the cache and
   * may not spend the customer's gateway on re-reading it, so the button is absent
   * rather than present-and-failing (`concept.md` §7 principle 8). */
  @property({ type: Boolean }) canRefresh = false;

  @state() private level: BrowseLevel = "catalogs";
  @state() private catalog = "";
  @state() private schema = "";
  @state() private table = "";
  @state() private q = "";
  @state() private searchColumns = false;

  @state() private result: BrowseResponse | null = null;
  @state() private loading = true;
  @state() private error: ApiError | null = null;
  @state() private refreshing = false;
  @state() private refreshNote = "";

  private searchTimer = 0;
  /** Guards against an out-of-order response overwriting a newer one: a fast
   * click-through of four levels resolves in whatever order the network chooses. */
  private seq = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .head {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .crumbs {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      min-width: 0;
    }
    .crumbs button {
      font: inherit;
      font-family: var(--bino-font-mono);
      color: var(--bino-primary);
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
      border-radius: var(--bino-radius-sm);
    }
    .crumbs button:hover {
      text-decoration: underline;
    }
    .crumbs button:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: 2px;
    }
    .crumbs .sep {
      color: var(--bino-text-muted);
    }
    .crumbs .here {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
      flex: 1 1 18rem;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .meta {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .meta + .meta {
      margin-top: var(--bino-space-xs);
    }
    .basis {
      margin: var(--bino-space-sm) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 78ch;
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
    }
    .drill {
      font: inherit;
      color: var(--bino-primary);
      background: none;
      border: 0;
      padding: 0;
      cursor: pointer;
    }
    .drill:hover {
      text-decoration: underline;
    }
    .drill:focus-visible {
      outline: var(--bino-focus-ring);
      outline-offset: 2px;
    }
    .error-message {
      margin: 0 0 var(--bino-space-sm);
      color: var(--bino-error);
    }
    .error-actions {
      margin-top: var(--bino-space-md);
    }
    .count {
      font-variant-numeric: tabular-nums;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback() {
    clearTimeout(this.searchTimer);
    super.disconnectedCallback();
  }

  private async load() {
    const mine = ++this.seq;
    this.loading = true;
    try {
      const res = await browseSchema(this.instanceId, {
        level: this.level,
        catalog: this.catalog || undefined,
        schema: this.schema || undefined,
        table: this.table || undefined,
        q: this.q || undefined,
        columns: this.level === "search" ? this.searchColumns : undefined,
      });
      if (mine !== this.seq) return;
      this.result = res;
      this.error = null;
    } catch (e) {
      if (mine !== this.seq) return;
      this.error = parseError(e);
      this.result = null;
    } finally {
      if (mine === this.seq) this.loading = false;
    }
  }

  /** goto moves to a level and clears everything below it. Clearing is the point:
   * keeping a stale `table` while moving to another schema would build a path that
   * addresses nothing and the API would answer an empty list for it. */
  private goto(level: BrowseLevel, catalog = "", schema = "", table = "") {
    this.level = level;
    this.catalog = catalog;
    this.schema = schema;
    this.table = table;
    if (level !== "search") this.q = "";
    void this.load();
  }

  private onSearch(value: string) {
    this.q = value;
    clearTimeout(this.searchTimer);
    const term = value.trim();
    this.searchTimer = window.setTimeout(() => {
      if (this.level === "search" && term.length < MIN_SEARCH_QUERY) {
        // Below the minimum the API refuses, so returning to the level the user
        // came from is better than showing them a 422 they did not ask for.
        this.goto("catalogs");
        return;
      }
      void this.load();
    }, SEARCH_DEBOUNCE_MS);
  }

  private startSearch() {
    this.level = "search";
    this.table = "";
    this.result = null;
    if (this.q.trim().length >= MIN_SEARCH_QUERY) void this.load();
  }

  private async refresh() {
    this.refreshing = true;
    this.refreshNote = "";
    try {
      await refreshSchema(this.instanceId);
      this.refreshNote = "The instance was re-read. Reloading this level.";
      await this.load();
    } catch (e) {
      const err = parseError(e);
      // A refusal here is not a browse failure: the level on screen is still
      // valid, only staler than the reader hoped. It is reported in place with the
      // code and the request id rather than replacing the tree with an error page.
      this.refreshNote = `${err.message} (${err.code}${err.requestId ? ` · ${err.requestId}` : ""})`;
    } finally {
      this.refreshing = false;
    }
  }

  /* ── cells ─────────────────────────────────────────────────────────────── */

  private toRows(nodes: BrowseNode[]): BnRow[] {
    const next = NEXT_LEVEL[this.level];
    return nodes.map((n, i) => {
      const drillTo = () => {
        if (this.level === "search") {
          // A search hit on a table opens its columns; a hit on a column opens the
          // columns of the table that holds it, with the term kept as a filter so
          // the reader lands on the row they clicked.
          this.q = n.table ? n.name : "";
          this.goto("columns", n.catalog ?? "", n.schema ?? "", n.table ?? n.name);
          return;
        }
        if (!next) return;
        this.goto(
          next,
          this.level === "catalogs" ? n.name : this.catalog,
          this.level === "schemas" ? n.name : this.schema,
          this.level === "tables" ? n.name : this.table,
        );
      };
      const clickable = this.level === "search" || (next !== undefined && (n.children ?? 0) > 0);
      const label = this.level === "search" ? qualify(n) : n.name;
      const cell = clickable
        ? html`<button class="drill" @click=${drillTo}>${label}</button>`
        : html`${label}`;
      return {
        id: `${i}:${label}`,
        name: html`${cell}${n.sample
          ? html`<bn-badge variant="info">Sample data</bn-badge>`
          : nothing}`,
        path: cell,
        kind: n.table ? "Column" : "Table",
        type: n.type ?? html`<span class="sub">—</span>`,
        children:
          n.children === undefined
            ? html`<span class="sub">—</span>`
            : html`<span class="count">${n.children}</span>`,
        age: n.status.fetched_at
          ? html`${relative(n.status.fetched_at)}${n.status.fresh
              ? nothing
              : html`<span class="sub">stale</span>`}`
          : html`<span class="sub">never read</span>`,
        node: n,
      };
    });
  }

  /* ── chrome ────────────────────────────────────────────────────────────── */

  private renderCrumbs(): TemplateResult {
    const crumbs: TemplateResult[] = [
      html`<button @click=${() => this.goto("catalogs")}>All catalogs</button>`,
    ];
    if (this.catalog && this.level !== "catalogs") {
      crumbs.push(
        html`<span class="sep">›</span>
          <button @click=${() => this.goto("schemas", this.catalog)}>${this.catalog}</button>`,
      );
    }
    if (this.schema && (this.level === "columns" || this.level === "tables")) {
      crumbs.push(
        html`<span class="sep">›</span>
          <button @click=${() => this.goto("tables", this.catalog, this.schema)}>
            ${this.schema}
          </button>`,
      );
    }
    if (this.table && this.level === "columns") {
      crumbs.push(html`<span class="sep">›</span><span class="here">${this.table}</span>`);
    }
    if (this.level === "search") {
      crumbs.push(html`<span class="sep">›</span><span class="here">search</span>`);
    }
    return html`<nav class="crumbs" aria-label="Schema path">${crumbs}</nav>`;
  }

  /** The freshness line. It reports the level's WORST age, which is the honest
   * badge for a mixed list — a search always mixes catalogs — and it names the
   * source, because a hand-pasted tree and a read from the instance are not equally
   * trustworthy. */
  private renderFreshness() {
    const status = this.result?.status;
    if (!status) return nothing;
    const parts: TemplateResult[] = [];
    if (status.fetched_at) {
      parts.push(html`Read ${relative(status.fetched_at)}`);
      if (status.source) parts.push(html` from <code>${status.source}</code>`);
    } else {
      parts.push(html`Nothing has been read from this instance yet`);
    }
    if (status.object_count) parts.push(html` · ${status.object_count} objects cached`);
    return html`<p class="meta">
      ${parts}
      ${status.fresh
        ? nothing
        : html` <bn-badge variant="warning">Stale</bn-badge>`}${status.truncated
        ? html` <bn-badge variant="warning">Incomplete</bn-badge>`
        : nothing}
    </p>`;
  }

  private renderError(err: ApiError) {
    return html`<div role="alert">
      <p class="error-message">${err.message}</p>
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote.
          </p>`}
      <div class="error-actions">
        <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
      </div>
    </div>`;
  }

  private renderEmpty() {
    // The two zero states are distinguishable and are distinguished, because the
    // remedies are opposite: clear the filter, or refresh the cache.
    if (this.q.trim() !== "") {
      return html`<bn-empty-state
        icon="⌕"
        heading="Nothing here matches that"
        message="No catalog, schema, table or column in the cache contains this term. A name the cache does not hold may still exist on your databases — refresh to be sure."
      >
        <bn-button slot="actions" @click=${() => this.onSearch("")}>Clear the filter</bn-button>
      </bn-empty-state>`;
    }
    const degraded = this.result?.status.degraded ?? true;
    return html`<bn-empty-state
      icon="∅"
      heading=${degraded ? "Nothing has been read from this instance yet" : "Nothing at this level"}
      message=${degraded
        ? "The introspection cache is empty, so there is nothing to browse and every policy editor's completions fall back to free text. Reading the schema needs a running instance."
        : "The cache holds this level and it is empty. That is what your instance reported the last time it was read."}
    >
      ${this.canRefresh
        ? html`<bn-button
            slot="actions"
            variant="primary"
            ?loading=${this.refreshing}
            @click=${() => void this.refresh()}
          >
            Refresh the schema
          </bn-button>`
        : nothing}
    </bn-empty-state>`;
  }

  override render() {
    const res = this.result;
    const columns = LEVEL_COLUMNS[this.level];
    const nodes = res?.nodes ?? [];

    return html`<bn-card heading="Schema browser">
      <div class="head">
        ${this.renderCrumbs()}
        <div class="actions">
          ${this.level === "search"
            ? html`<bn-checkbox
                ?checked=${this.searchColumns}
                @change=${(e: CustomEvent<{ checked: boolean }>) => {
                  this.searchColumns = e.detail.checked;
                  void this.load();
                }}
                >Match column names too</bn-checkbox
              >`
            : html`<bn-button @click=${() => this.startSearch()}>Search everything</bn-button>`}
          ${this.canRefresh
            ? html`<bn-button ?loading=${this.refreshing} @click=${() => void this.refresh()}>
                Refresh
              </bn-button>`
            : nothing}
        </div>
      </div>

      <div class="toolbar">
        <bn-field
          label=${this.level === "search" ? "Find a table or column" : "Filter this level"}
          hint=${this.level === "search"
            ? `At least ${MIN_SEARCH_QUERY} characters. A substring, not a pattern — a wildcard here is a literal asterisk.`
            : "A case-insensitive substring of the names on this level."}
        >
          <bn-input
            .value=${this.q}
            placeholder=${this.level === "search" ? "customers" : "orders"}
            @input=${(e: CustomEvent<{ value: string }>) => this.onSearch(e.detail.value)}
          ></bn-input>
        </bn-field>
      </div>

      ${this.error
        ? this.renderError(this.error)
        : html`
            <bn-table
              caption=${`Schema · ${this.level}`}
              .columns=${columns}
              .rows=${this.toRows(nodes)}
              ?loading=${this.loading}
              @row-click=${(e: CustomEvent<BnRowClickDetail>) => e.stopPropagation()}
            >
              <div slot="empty">${this.loading ? nothing : this.renderEmpty()}</div>
            </bn-table>
            ${res && res.truncated
              ? html`<p class="meta">
                  Showing ${nodes.length} of ${res.total}. Narrow the filter to see the rest — this
                  is a page boundary, not missing data.
                </p>`
              : nothing}
            ${this.renderFreshness()}
            ${res ? html`<p class="basis">${res.basis}</p>` : nothing}
            ${this.refreshNote ? html`<p class="meta" role="status">${this.refreshNote}</p>` : nothing}
          `}
    </bn-card>`;
  }
}

/** qualify renders a search hit's dotted path. It is display only: a column hit
 * produces four segments, which is not a `catalog.schema.table` reference any
 * policy field would accept. */
function qualify(n: BrowseNode): string {
  return [n.catalog, n.schema, n.table, n.name].filter((p) => p).join(".");
}

/** relative renders an age the way a reader checking freshness reads one, falling
 * back to the raw value rather than to "Invalid Date" if the server sends something
 * this bundle cannot parse. */
function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return new Date(then).toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "schema-browser": SchemaBrowser;
  }
}
